<template>
  <div>
    <d-card-header class="border-bottom border-top d-flex rounded-0">
      <h6 class="mr-4 my-1 mb-1">Przedstawiciele</h6>

      <d-button
        class="ml-auto my-auto"
        @click.prevent="showAddRepPopup"
        theme="primary"
        :disabled="loadError || !allDataLoaded || isActionInProgress">
        <i class="material-icons mr-1">add</i>Dodaj przedstawiciela
      </d-button>
    </d-card-header>
    <d-alert
      v-if="loadError"
      show
      theme="warning"
      class="my-4">
      Wczytywanie danych nie powiodło się.
      <a
        href="javascript:window.location.reload();"
        class="alert-link">
        Odśwież stronę
      </a>
      aby spróbować ponownie.
    </d-alert>
    <div
      v-else
      class="position-relative pt-3">
      <div
        v-if="!allDataLoaded"
        class="overlay-loading rounded-0">
        <h2 class="preloader isLoading">Trwa ładowanie</h2>
      </div>

      <table class="table mb-0 border-top-0 responsive-table">
        <thead>
          <tr>
            <th scope="col" class="border-0">Nazwa</th>
            <th scope="col" class="border-0">Nr telefonu</th>
            <th scope="col" class="border-0">Email</th>
            <th scope="col" class="border-0 text-center" style="width: 330px;">Akcje</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(item, index) in representatives"
            :key="`representative-` + index">
            <td>
              <dt>Nazwa</dt>
              <dd>{{ item.name }} {{ item.surname }}</dd>
            </td>
            <td>
              <dt>Nr telefonu</dt>
              <dd>
                {{ item.phone || '-' }}
              </dd>
            </td>
            <td>
              <dt>Email</dt>
              <dd>
                {{ item.email || '-' }}
              </dd>
            </td>
            <td class="text-center">
              <dt>Akcje</dt>
              <dd>
                <d-button
                  class="ml-auto my-auto"
                  @click.prevent="editRepresentative(item, index)"
                  theme="success"
                  :disabled="isActionInProgress">
                  <i class="material-icons">edit</i>Edytuj
                </d-button>
                <d-button
                  class="ml-3 my-auto"
                  @click.prevent="onRemoveRepresentative(index, item.id)"
                  theme="danger"
                  :disabled="isActionInProgress">
                  <i class="material-icons">clear</i>Usuń z listy
                </d-button>
                <d-button
                  class="ml-3 my-auto"
                  @click.prevent="onRemoveRepresentative(index, item.id, true)"
                  theme="danger"
                  outline
                  :disabled="isActionInProgress">
                  <i class="material-icons">delete</i>Usuń
                </d-button>
              </dd>
            </td>
          </tr>
          <tr v-if="!representatives.length">
            <td
              :colspan="4"
              class="text-center text-info">
              Brak przedstawicieli&hellip;
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <RepresentativeFormModal
      ref="representativeFormModal"
      :all-client-representatives-ids="currentlySelectedReps"
      :all-representatives-lite="allRepresentativesLite"
      @add-representative="addRepresentative" />
  </div>
</template>

<script>
import ClientUtils from '@/utils/client.js';
import FormUtils from '@/utils/FormUtils.js';
import ListUtils from '@/utils/ListUtils.js';
import RepresentativeFormModal from '@/components/client/RepresentativeFormModal.vue';

export default {
  name: 'PersonalDataRepresentatives',
  components: {
    RepresentativeFormModal
  },
  props: {
    clientRepresentatives: {
      default: () => [],
      type: Array
    }
  },
  computed: {
    allDataLoaded () {
      return this.allRepresentativesLiteLoaded;
    },
    ClientUtils () {
      return ClientUtils;
    },
    currentlySelectedReps () {
      return this.representatives.map(item => item.id).filter(item => !!item);
    }
  },
  data () {
    return {
      allRepresentativesLite: [],
      allRepresentativesLiteLoaded: true, // initially this fla is set to true because we might not be fetching lite
      clientRepresentativesLoaded: false,
      hasInitialRepresentatives: false,
      isActionInProgress: false,
      loadError: false,
      representatives: []
    }
  },
  mounted () {
    this.representatives = JSON.parse(JSON.stringify(this.clientRepresentatives));
  },
  methods: {
    showAddRepPopup () {
      if (!this.allRepresentativesLite.length) {
        this.allRepresentativesLiteLoaded = false;
        this.debouncedLoadAllRepresentativesLite();
      }

      this.$refs.representativeFormModal.showModal();
    },
    debouncedLoadAllRepresentativesLite: _.debounce(function () {
      this.loadAllRepresentativesLite();
    }, 1000),
    loadAllRepresentativesLite () {
      this.allRepresentativesLiteLoaded = false;

      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/representatives/get-representatives-lite',
        method: 'post',
        outputKey: 'allRepresentativesLite',
        loadedKey: 'allRepresentativesLiteLoaded',
        errorKey: 'loadError',
        noPagination: true
      });
    },
    fetchCurrentRepresentatives () {
      this.clientRepresentativesLoaded = false;
      let where = {};
      if (this.clientRepresentatives.length) {
        where.ids = this.clientRepresentatives;
      }
      FormUtils.loadAdditionalData(this, {
        endpoint: '/api/representatives/get-representatives',
        method: 'post',
        outputKey: 'representatives',
        loadedKey: 'clientRepresentativesLoaded',
        errorKey: 'loadError',
        noPagination: false,
        params: { where },
      });
    },
    onRemoveRepresentative (repIndex, repID, isHardRemove = false) {
      this.isActionInProgress = true;

      if (!isHardRemove) {
        this.representatives.splice(repIndex, 1);
        this.isActionInProgress = false;
        return;
      }

      this.removeRepresentative(repID, repIndex);
    },
    removeRepresentative (repID, repIndex) {
      this.isActionInProgress = true;

      ListUtils.removeItem(this, {
        endpointBase: '/api/representatives/remove-representative',
        id: repID,
        successTitle: 'Przedstawiciel został usunięty',
        successText: '',
        confirmTitle: 'Uwaga',
        confirmText: 'Czy na pewno chcesz usunąć przedstawiciela?<br>Zostanie on usunięty z bazy danych, a więc zarówno dla tego, jak i dla innych klientów.',
        errorTitle: 'Wystąpił błąd',
        errorText: 'Usuwanie przedstawiciela nie powiodło się. Spróbuj ponownie.',
        successAction: () => {
          this.representatives.splice(repIndex, 1);
          this.debouncedLoadAllRepresentativesLite();
          this.isActionInProgress = false;
        },
        cancelAction: () => {
          this.isActionInProgress = false;
        },
        failAction: () => {
          this.isActionInProgress = false;
        },
      });
    },
    addRepresentative (representative, representativeIndex = null, shouldReload = false) {
      if (representativeIndex === null) {
        this.representatives.push(JSON.parse(JSON.stringify(representative)));
      } else {
        this.representatives[representativeIndex] = JSON.parse(JSON.stringify(representative));
      }

      if (shouldReload) {
        this.debouncedLoadAllRepresentativesLite();
      }
    },
    editRepresentative (rep, repIndex) {
      this.$refs.representativeFormModal.showModal(rep, repIndex);
    },
    getRepresentatives () {
      return this.currentlySelectedReps;
    }
  }
}
</script>

<style scoped>
.dropdown-toggle::after {
  margin-left: 1.5em;
}
</style>